/**
 * Keeps `modelGroup` in sync with `ctx.session.model`.
 *
 * Deliberately dumb: it adds and removes exactly one object (`session.model.root`) and never
 * decorates it. Anything the editor draws *about* the model goes in the overlay group, because
 * the GLB exporter serializes the model root verbatim — a debug helper parented here would end
 * up in the user's exported character file.
 */

import { Box3 } from 'three';
import type { Group, Object3D } from 'three';

import type { EditorContext } from '../context';

export interface ModelLayer {
  /** Re-read `session.model` and swap what is in the scene. Returns true if anything changed. */
  sync(): boolean;
  /** World bounds of the current model, or null when nothing is loaded. */
  bounds(): Box3 | null;
  dispose(): void;
}

export function createModelLayer(modelGroup: Group, ctx: EditorContext): ModelLayer {
  let current: Group | null = null;

  function sync(): boolean {
    const next = ctx.session.model?.root ?? null;
    if (next === current) return false;

    if (current) modelGroup.remove(current);
    current = next;
    if (current) {
      // No pose is applied outside ANIMATE, so what lands on screen here is the rest pose —
      // which is exactly what RIG and MAP need to show.
      modelGroup.add(current);
      current.updateMatrixWorld(true);
    }
    return true;
  }

  function bounds(): Box3 | null {
    const model = ctx.session.model;
    if (!model || !current) return null;
    // Recompute rather than trusting `model.bounds`: after skinning the displayed mesh is a
    // different object, and after a pose the model no longer occupies its rest-pose box.
    const box = new Box3().setFromObject(current);
    return box.isEmpty() ? null : box;
  }

  function dispose(): void {
    if (current) modelGroup.remove(current);
    current = null;
  }

  return { sync, bounds, dispose };
}

// ---------------------------------------------------------------------------
// Companions
// ---------------------------------------------------------------------------

/** Side-by-side gap as a multiple of the widest character in the scene. */
const COMPANION_SPACING = 1.2;

export interface CompanionLayer {
  /**
   * Re-read `session.companions` and lay them out beside the open model. Call on
   * `'companions-changed'` (and whenever the open model's bounds change, since they set the
   * spacing). `modelBounds` is `modelLayer.bounds()`.
   */
  sync(modelBounds: Box3 | null): void;
  /** World bounds of every attached companion, or null when there are none. */
  bounds(): Box3 | null;
  /** Follows the "Mesh" display switch together with `view.modelGroup`. */
  setVisible(visible: boolean): void;
  dispose(): void;
}

/** X extent of a box, or 0 for "no box". */
function widthOf(box: Box3 | null): number {
  return box ? Math.max(0, box.max.x - box.min.x) : 0;
}

/**
 * Keeps a dedicated group in sync with `session.companions`.
 *
 * Its own group, never `modelGroup` (the exporter walks the open model) and never `overlayGroup`
 * (editor-only helpers): a companion is a real character that is neither the user's model nor a
 * gizmo, so it needs a third home.
 *
 * Layout is "one row to the right", spaced by the widest body present, so two characters of very
 * different sizes still stand clear of each other. Companion roots are never disposed here — the
 * preview controller owns them.
 */
export function createCompanionLayer(companionGroup: Group, ctx: EditorContext): CompanionLayer {
  const attached: Object3D[] = [];

  function detachAll(): void {
    for (const root of attached.splice(0)) companionGroup.remove(root);
  }

  function sync(modelBounds: Box3 | null): void {
    const wanted = ctx.session.companions.map((companion) => companion.root);

    const same =
      wanted.length === attached.length && wanted.every((root, i) => root === attached[i]);
    if (!same) {
      detachAll();
      for (const root of wanted) {
        companionGroup.add(root);
        attached.push(root);
      }
    }
    if (attached.length === 0) return;

    // Two passes: measure at the origin, then place. Measuring an already-offset root would fold
    // the previous layout's X into the spacing and walk the row further right every sync.
    let widest = widthOf(modelBounds);
    for (const root of attached) {
      root.position.x = 0;
      root.updateMatrixWorld(true);
      widest = Math.max(widest, widthOf(new Box3().setFromObject(root)));
    }

    const spacing = COMPANION_SPACING * Math.max(widest, 0.01);
    for (let i = 0; i < attached.length; i++) {
      attached[i].position.x = (i + 1) * spacing;
      attached[i].updateMatrixWorld(true);
    }
  }

  function bounds(): Box3 | null {
    if (attached.length === 0) return null;
    const box = new Box3();
    for (const root of attached) box.union(new Box3().setFromObject(root));
    return box.isEmpty() ? null : box;
  }

  return {
    sync,
    bounds,
    setVisible(visible: boolean): void {
      companionGroup.visible = visible;
    },
    dispose(): void {
      detachAll();
    },
  };
}
